import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Bar, Area, AreaChart, ComposedChart, BarChart } from 'recharts';
import { useAuth } from '../AuthContext';
import { colors } from '../components/SharedComponents';
import AnomalyExclusionTag from '../components/AnomalyExclusionTag';
import MetricFlagTag from '../components/MetricFlagTag';
import MetricInfo from '../components/MetricInfo';
import MarkerLegend from '../components/MarkerLegend';
import { API_BASE } from '../config';
import './Analytics.linear.css';

const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : (plural || singular + 's')}`;

// Lane 121 items 4+6: the counterfactual / possible-unit-basis explainer shown on a
// Net Impact row whose impact% exceeds +/-100% OR whose |impact| exceeds a year's spend.
// Verbatim approved copy (docs/audits/2026-07-14_lane121_ppi-display-honesty_plan.md sec 2.3).
const IMPACT_VERIFY_TOOLTIP = "This number is real -- a big price drop, or a pack-size change recorded as a price, can push it past +/-100% or beyond a year's spend. Verify the source POs.";

// Lane 383 (Ryan, staging QA 2026-07-25): the Category rollup renders only the
// categories at or above this share of trailing spend by default; the
// sub-threshold tail folds behind an expand control. XMEK carries ~60 categories,
// most a fraction of a percent, and the tail buried the page.
//
// RYAN-DECIDES CONSTANT -- change this ONE number to move the cut (0.5, 2.0, ...);
// nothing else has to move. Compared against the server's `spend_pct` (0-100 scale,
// already rounded to 1dp server-side), so the cut agrees with the percent the row
// displays. `>=` is deliberate: everything HIDDEN is then literally "under 1%",
// which is what the expand control claims.
//
// DISPLAY-ONLY. Every figure in the summary strip above the table is computed
// SERVER-side over ALL categories (category_count / total_spend / ppi_measured in
// routes/analytics.py get_category_rollup), so this cut hides rows and can never
// move a total. Pinned by Analytics.categoryRollup.test.js.
const CATEGORY_ROLLUP_MIN_SPEND_PCT = 1.0;

// Lane 396: the Spend-% cell's precision is COUPLED to the fold above -- the percent
// a reader sees must never contradict which side of the cut the row is on. The old
// `.toFixed(0)` collided with the 1-dp threshold: a 0.6%-of-spend category displayed
// "1%" while correctly folding away as "under 1% of spend". Row and control disagreed.
//
// 1 dp is LOSSLESS here -- routes/analytics.py rounds spend_pct to 1 dp
// (`round(row["spend"] / total_spend * 100, 1)`), so the rendered number IS the number
// the fold compares. A trailing ".0" is then dropped so whole shares still read "28%",
// not "28.0%" (stripping trailing zeros cannot change a value, so it cannot move the
// displayed percent across the cut).
//
// The escalation loop is the ANTI-DRIFT guard, not decoration. If the payload ever
// carries a finer-grained spend_pct (a server-side change) or the threshold moves to a
// value 1 dp cannot express, rounding could again put a row's displayed percent on the
// wrong side of the cut. Escalating precision until the displayed and true values agree
// on `>= CATEGORY_ROLLUP_MIN_SPEND_PCT` makes that contradiction UNREACHABLE rather than
// merely absent today -- the display and the fold cannot drift apart again.
function formatCategorySpendPct(pct) {
  const value = pct || 0;
  const clearsCut = (x) => x >= CATEGORY_ROLLUP_MIN_SPEND_PCT;
  let dp = 1;
  let text = value.toFixed(dp);
  while (dp < 4 && clearsCut(parseFloat(text)) !== clearsCut(value)) {
    dp += 1;
    text = value.toFixed(dp);
  }
  // "28.0" -> "28", "0.6" -> "0.6". Guarded so a whole number like "10" is untouched.
  if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '');
  return `${text}%`;
}

function Analytics() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [spendWaterfall, setSpendWaterfall] = useState(null);
  const [topMaterials, setTopMaterials] = useState([]);
  const [singleSourceMaterials, setSingleSourceMaterials] = useState([]);
  const [singleSourceTotal, setSingleSourceTotal] = useState(0);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [ppvTrend, setPpvTrend] = useState([]);
  const [riskSummary, setRiskSummary] = useState(null);
  const [error, setError] = useState(null);

  // Site / location filter (Lane 5) — re-scopes the PO-spend cards only.
  // '' = "All sites" (default; byte-identical to pre-filter behavior).
  const [siteFilter, setSiteFilter] = useState('');
  const [siteOptions, setSiteOptions] = useState([]);

  // Price Movers state (Feature 19) — independent loading
  const [priceMovers, setPriceMovers] = useState(null);
  const [priceMoversLoading, setPriceMoversLoading] = useState(false);
  // Lane 862: a failed fetch used to render as this section's EMPTY state --
  // "No significant price changes detected", which is a statement of fact about
  // the buyer's data that we had not established. See runSectionFetch below.
  const [priceMoversFailed, setPriceMoversFailed] = useState(false);
  const [priceMoversMonths, setPriceMoversMonths] = useState(24);
  const [priceMoversSortKey, setPriceMoversSortKey] = useState('change_pct');
  const [priceMoversSortDir, setPriceMoversSortDir] = useState('desc');

  // Supplier YoY Price Impact state (Feature 18) — independent loading
  const [supplierImpact, setSupplierImpact] = useState(null);
  const [supplierImpactLoading, setSupplierImpactLoading] = useState(false);
  // Lane 862: same shape as priceMoversFailed -- a failure used to read as "No
  // supplier price impact data available for the selected period."
  const [supplierImpactFailed, setSupplierImpactFailed] = useState(false);
  const [supplierImpactMonths, setSupplierImpactMonths] = useState(12);
  const [supplierImpactSortKey, setSupplierImpactSortKey] = useState('current_spend');
  const [supplierImpactSortDir, setSupplierImpactSortDir] = useState('desc');

  // PPI divergence rollup state (Lane 288) — independent loading. Flag-gated
  // (PPI_DIVERGENCE_VIEW_ENABLED): the endpoint returns {enabled:false} when OFF,
  // so the whole section stays hidden and the page is byte-identical to today.
  const [ppiDivergence, setPpiDivergence] = useState(null);
  const [ppiDivergenceLoading, setPpiDivergenceLoading] = useState(false);
  // Lane 860: a failed fetch used to render exactly the same nothing as a
  // still-in-flight one. This separates them -- see the four-state note at the
  // section's render site.
  const [ppiDivergenceFailed, setPpiDivergenceFailed] = useState(false);
  const [ppiDivergenceMonths, setPpiDivergenceMonths] = useState(12);
  const [ppiDivergenceDir, setPpiDivergenceDir] = useState('all'); // all | above | below
  const [ppiDivergenceSortKey, setPpiDivergenceSortKey] = useState(null); // null = server |divergence| order
  const [ppiDivergenceSortDir, setPpiDivergenceSortDir] = useState('desc');

  // Category rollup (Lane 292): flag-gated (CATEGORY_ROLLUP_VIEW_ENABLED). The
  // endpoint returns {enabled:false} when OFF, so the section never renders and
  // the page is byte-identical to today.
  const [categoryRollup, setCategoryRollup] = useState(null);
  const [categoryRollupLoading, setCategoryRollupLoading] = useState(false);
  // Lane 862: see the four-state note at this section's render site -- the gate
  // was `categoryRollup?.enabled` alone, exactly the shape Lane 860 fixed on PPI
  // divergence, so its loading branch was unreachable code.
  const [categoryRollupFailed, setCategoryRollupFailed] = useState(false);
  const [categoryRollupMonths, setCategoryRollupMonths] = useState(12);
  const [categoryRollupSortKey, setCategoryRollupSortKey] = useState(null); // null = server spend order
  const [categoryRollupSortDir, setCategoryRollupSortDir] = useState('desc');
  // Lane 383: false = show only categories >= CATEGORY_ROLLUP_MIN_SPEND_PCT of spend.
  const [categoryRollupShowAll, setCategoryRollupShowAll] = useState(false);

  useEffect(() => {
    fetchSiteOptions();
    fetchAllData();
  }, [token]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Append ?site= only for the PO-spend endpoints; '' leaves the URL untouched
  // (so "All sites" sends the exact request it sends today).
  const withSite = (url, site) =>
    site ? `${url}${url.includes('?') ? '&' : '?'}site=${encodeURIComponent(site)}` : url;

  const fetchSiteOptions = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/purchase-sites`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSiteOptions(data.sites || []);
      }
    } catch (err) {
      console.log('Site options fetch failed:', err.message);
    }
  };

  // The 7 dashboard cards. The 5 PO-spend ones take ?site=; ppv-trend and
  // risk-summary have no manufacturing_site dimension and stay company-wide.
  const fetchDashboards = async (site) => {
    const headers = getAuthHeaders();
    const [metricsRes, waterfallRes, materialsRes, singleSourceRes, suppliersRes, trendRes, riskRes] = await Promise.all([
      fetch(withSite(`${API_BASE}/api/dashboard/metrics`, site), { headers }),
      fetch(withSite(`${API_BASE}/api/dashboard/spend-waterfall`, site), { headers }),
      fetch(withSite(`${API_BASE}/api/dashboard/top-materials`, site), { headers }),
      fetch(withSite(`${API_BASE}/api/dashboard/single-source-materials`, site), { headers }),
      fetch(withSite(`${API_BASE}/api/dashboard/top-suppliers`, site), { headers }),
      fetch(`${API_BASE}/api/dashboard/ppv-trend`, { headers }),
      fetch(`${API_BASE}/api/dashboard/risk-summary`, { headers }).catch(() => ({ json: () => null }))
    ]);

    setMetrics(await metricsRes.json());
    setSpendWaterfall(await waterfallRes.json());
    setTopMaterials(await materialsRes.json());
    const singleSourceData = await singleSourceRes.json();
    setSingleSourceMaterials(singleSourceData.materials || []);
    setSingleSourceTotal(singleSourceData.total_count || 0);
    setTopSuppliers(await suppliersRes.json());
    setPpvTrend(await trendRes.json());

    const riskData = await riskRes.json();
    if (riskData) setRiskSummary(riskData);
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await fetchDashboards(siteFilter);

      // Kick off independent analytics fetches (non-blocking, not site-scoped in v1)
      fetchPriceMovers(priceMoversMonths);
      fetchSupplierImpact(supplierImpactMonths);
      fetchPpiDivergence(ppiDivergenceMonths);
      fetchCategoryRollup(categoryRollupMonths);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Unable to load analytics data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSiteChange = async (e) => {
    const newSite = e.target.value;
    setSiteFilter(newSite);
    try {
      await fetchDashboards(newSite);
    } catch (err) {
      console.log('Site re-scope fetch failed:', err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Lane 862: ONE async-state runner for every independently-fetched section.
  //
  // All four section endpoints in backend/routes/analytics.py answer failure in
  // the same three shapes: a non-2xx, a thrown network error, and -- the one
  // that actually misled a user -- an HTTP 200 carrying an `error` key, which is
  // how each endpoint's outer `except Exception` reports a COMPUTE failure
  // (`{"suppliers": [], "error": str(e)}` and friends).
  //
  // Every section used to spell its own bare `if (res.ok)` with no else, so two
  // of those three shapes left the section's data state untouched and fell
  // through to its EMPTY message -- telling a buyer "no significant price
  // changes detected" / "no supplier price impact data" / "no category spend to
  // roll up" when the truth was that we had failed to work it out. That is the
  // product asserting something about the customer's data that we never
  // established. Lane 860 fixed exactly this on PPI divergence; this runner is
  // that fix made shared, so the four call sites cannot drift apart again.
  //
  // `failed` is a THIRD state, deliberately distinct from loading and from
  // empty: a reader acts differently on "wait", "retry" and "there is nothing
  // here", and collapsing any two of them loses information the reader needs.
  const runSectionFetch = async ({ url, label, setData, setLoading, setFailed }) => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setData(data);
        if (data && data.error) setFailed(true);
      } else {
        setFailed(true);
      }
    } catch (err) {
      console.log(`${label} fetch failed:`, err.message);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceMovers = async (months) => {
    if (!token) return;
    await runSectionFetch({
      url: `${API_BASE}/api/analytics/price-movers?months=${months}`,
      label: 'Price movers',
      setData: setPriceMovers,
      setLoading: setPriceMoversLoading,
      setFailed: setPriceMoversFailed,
    });
  };

  const handlePriceMoversMonthsChange = (newMonths) => {
    setPriceMoversMonths(newMonths);
    fetchPriceMovers(newMonths);
  };

  const handlePriceMoversSort = (key) => {
    if (key === priceMoversSortKey) {
      setPriceMoversSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setPriceMoversSortKey(key);
      setPriceMoversSortDir(key === 'change_pct' || key === 'spend_impact' || key === 'l12m_spend' ? 'desc' : 'asc');
    }
  };

  const fetchSupplierImpact = async (months) => {
    if (!token) return;
    await runSectionFetch({
      url: `${API_BASE}/api/analytics/supplier-price-impact-summary?months=${months}`,
      label: 'Supplier impact',
      setData: setSupplierImpact,
      setLoading: setSupplierImpactLoading,
      setFailed: setSupplierImpactFailed,
    });
  };

  const handleSupplierImpactMonthsChange = (newMonths) => {
    setSupplierImpactMonths(newMonths);
    fetchSupplierImpact(newMonths);
  };

  const handleSupplierImpactSort = (key) => {
    if (key === supplierImpactSortKey) {
      setSupplierImpactSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSupplierImpactSortKey(key);
      setSupplierImpactSortDir(key === 'total_impact' || key === 'current_spend' ? 'desc' : 'asc');
    }
  };

  // Lane 860 wrote this logic inline here; Lane 862 lifted it verbatim into
  // runSectionFetch when three sibling sections turned out to need the identical
  // thing. Behaviour is unchanged -- the 200-plus-`error` compute failure from
  // get_ppi_divergence still reads as a failure, never as "no profiles yet".
  const fetchPpiDivergence = async (months) => {
    if (!token) return;
    await runSectionFetch({
      url: `${API_BASE}/api/analytics/ppi-divergence?months=${months}`,
      label: 'PPI divergence',
      setData: setPpiDivergence,
      setLoading: setPpiDivergenceLoading,
      setFailed: setPpiDivergenceFailed,
    });
  };

  const handlePpiDivergenceMonthsChange = (newMonths) => {
    setPpiDivergenceMonths(newMonths);
    fetchPpiDivergence(newMonths);
  };

  const handlePpiDivergenceSort = (key) => {
    // Clicking a header opts out of the server |divergence| order into an
    // explicit client sort; clicking the active column flips direction.
    if (key === ppiDivergenceSortKey) {
      setPpiDivergenceSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setPpiDivergenceSortKey(key);
      // Numeric columns default to desc (biggest first); supplier name to asc.
      setPpiDivergenceSortDir(key === 'supplier_name' ? 'asc' : 'desc');
    }
  };

  const fetchCategoryRollup = async (months) => {
    if (!token) return;
    await runSectionFetch({
      url: `${API_BASE}/api/analytics/category-rollup?months=${months}`,
      label: 'Category rollup',
      setData: setCategoryRollup,
      setLoading: setCategoryRollupLoading,
      setFailed: setCategoryRollupFailed,
    });
  };

  const handleCategoryRollupMonthsChange = (newMonths) => {
    setCategoryRollupMonths(newMonths);
    // Lane 383: a new window is a new category set -- re-fold to the >=1% view.
    setCategoryRollupShowAll(false);
    fetchCategoryRollup(newMonths);
  };

  const handleCategoryRollupSort = (key) => {
    // Clicking a header opts out of the server spend order into an explicit client
    // sort; clicking the active column flips direction (Lane 121 signed idiom).
    if (key === categoryRollupSortKey) {
      setCategoryRollupSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setCategoryRollupSortKey(key);
      setCategoryRollupSortDir(key === 'category' ? 'asc' : 'desc');
    }
  };

  const formatCurrency = (value) => {
    if (value == null) return '$0';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(0)}K`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  const formatCurrencyFull = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const scrollToSingleSource = () => {
    document.getElementById('single-source-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="an">
        <div className="an-wrap">
          <div className="an-loading">
            <div style={{ textAlign: 'center' }}>
              <div className="spin">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              </div>
              <div>Loading analytics…</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="an">
        <div className="an-wrap">
          <header className="an-head">
            <div>
              <h1>Analytics</h1>
              <div className="sub">Portfolio performance and insights</div>
            </div>
          </header>
          <div className="an-error">
            <div className="msg">{error}</div>
            <button className="an-btn pri" onClick={fetchAllData}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const ppvStatus = (metrics?.total_ppv || 0) >= 0 ? 'under' : 'over';
  const ppvAmount = formatCurrency(Math.abs(metrics?.total_ppv || 0));
  const coveragePct = riskSummary?.contract_coverage?.coverage_percent?.toFixed(0) || 0;
  const singleSourceCount = metrics?.single_source_count || 0;

  // Lane 306 (QA-4 follow-up, Ryan decision 2026-07-22): the "Material Spend by
  // Category" chart shows only the TOP 10 categories by spend so it reads clean/
  // compact — reverting Lane 301's all-categories dynamic-height approach, which
  // made the chart ~1680px tall for XMEK's ~60 categories. The backend already
  // returns categories spend-desc (analytics.py ORDER BY spend DESC); we
  // defensively re-sort on a COPY then slice(0,10) so the top 10 hold regardless
  // of ordering and the state array is never mutated. Display-only — no data change.
  const CATEGORY_CHART_LIMIT = 10;
  const categoryTotalCount = spendWaterfall?.category_count || 0;
  const categoryChartData = [...(spendWaterfall?.waterfall_data || [])]
    .sort((a, b) => (b.spend || 0) - (a.spend || 0))
    .slice(0, CATEGORY_CHART_LIMIT)
    // Lane 1385: each bar shows the shared category name (server category_label).
    .map((d) => ({ ...d, category_name: d.category_label || d.category }));
  const categoryChartCapped = categoryTotalCount > CATEGORY_CHART_LIMIT;

  // Price Movers subtitle (increases / decreases summary) — mirrors the pre-Linear IIFE.
  let moversSubtitle = null;
  if (priceMoversLoading) {
    moversSubtitle = 'Loading…';
  } else if (priceMoversFailed) {
    // Lane 862: the summary is derived from whatever payload we last held. After
    // a failed re-fetch that payload is stale, and a subtitle counting the OLD
    // increases/decreases beside a "could not be loaded" body would present
    // superseded numbers as current. No subtitle is the honest render.
    moversSubtitle = null;
  } else if (priceMovers && priceMovers.movers?.length) {
    const s = priceMovers.summary;
    const incPart = s.increases > 0 ? `${s.increases} with price increases` : '';
    const decPart = s.decreases > 0 ? `${s.decreases} with decreases` : '';
    moversSubtitle = [incPart, decPart].filter(Boolean).join(' · ');
  }

  // Movers header columns (v2 + hygiene conditionals preserved verbatim from pre-Linear).
  // Lane 227 (W1-2): material number/name are SEPARATE columns matching the Top
  // Materials table exactly — the number is always fully visible (an-mno, no cap),
  // the name truncates to a single line (an-cellname ellipsis, never a two-line wrap).
  const moversCols = priceMovers ? [
    { key: 'material_number', label: 'Material #', align: 'left' },
    { key: 'material_name', label: 'Material Name', align: 'left' },
    { key: 'material_type', label: 'Category', align: 'left' },
    { key: 'supplier_name', label: 'Supplier', align: 'left' },
    // Lane 227 (W1-2): "Annual Spend (L12M)" -> "Annual Spend" -- "Annual" already
    // means the trailing 12 months (this column is fixed L12M regardless of the movers
    // period selector), so "(L12M)" was redundant width. Dropping it helps the 9-column
    // v2 table fit without clipping the right-most "Annual Impact" header.
    { key: 'l12m_spend', label: 'Annual Spend', align: 'right' },
    // Lane 135: header hovers ride the MetricInfo firewall and are attached ONLY
    // under the hygiene path, so the registry prose always describes the leg
    // construction actually running. Flag OFF = no glyph = today's header.
    { key: 'early_avg_price', label: 'Early Avg', align: 'right', metricKey: priceMovers.movers_hygiene ? 'movers_early_avg' : undefined },
    { key: 'recent_avg_price', label: 'Recent Avg', align: 'right', metricKey: priceMovers.movers_hygiene ? 'movers_recent_avg' : undefined },
    { key: 'change_pct', label: 'Change %', align: 'right' },
    // Spend Impact restored only when price-impact v2 is on. Lane 118 (2C): under
    // the hygiene path the figure is the ANNUALIZED impact. Lane 149 (C2): the raw
    // title tooltip is gone — the MetricInfo header hover carries that sentence.
    ...(priceMovers.price_impact_v2 ? [
      {
        key: 'spend_impact',
        label: priceMovers.movers_hygiene ? 'Annual Impact' : 'Spend Impact',
        align: 'right',
        metricKey: priceMovers.movers_hygiene ? 'movers_annual_impact' : undefined,
      },
    ] : []),
  ] : [];

  const sortedMovers = priceMovers ? [...priceMovers.movers].sort((a, b) => {
    // l12m_spend: 0/null values sort to the bottom regardless of direction
    if (priceMoversSortKey === 'l12m_spend') {
      const aRaw = a.l12m_spend || 0;
      const bRaw = b.l12m_spend || 0;
      if (aRaw === 0 && bRaw !== 0) return 1;
      if (bRaw === 0 && aRaw !== 0) return -1;
      return priceMoversSortDir === 'asc' ? aRaw - bRaw : bRaw - aRaw;
    }
    // Lane 121 item 7: the impact column sorts by SIGNED value (not magnitude),
    // so desc = biggest increases first, asc = biggest decreases first.
    const aVal = priceMoversSortKey === 'spend_impact' ? (a[priceMoversSortKey] || 0) : (a[priceMoversSortKey] || '');
    const bVal = priceMoversSortKey === 'spend_impact' ? (b[priceMoversSortKey] || 0) : (b[priceMoversSortKey] || '');
    if (typeof aVal === 'string') {
      return priceMoversSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return priceMoversSortDir === 'asc' ? aVal - bVal : bVal - aVal;
  }) : [];

  // Supplier YoY header columns (v2 conditionals preserved verbatim).
  const yoyCols = supplierImpact ? [
    { key: 'supplier_name', label: 'Supplier', align: 'left' },
    { key: 'current_spend', label: 'Current Spend', align: 'right' },
    { key: 'materials_increased', label: 'Increases', align: 'right' },
    { key: 'materials_decreased', label: 'Decreases', align: 'right' },
    // Impact columns restored only when price-impact v2 is on (flag OFF => hidden
    // => pixel-identical to today). Lane 135: each carries a MetricInfo header hover.
    ...(supplierImpact.price_impact_v2 ? [
      { key: 'total_impact', label: 'Net Impact', align: 'right', metricKey: 'supplier_impact_net' },
      { key: 'impact_pct', label: 'Impact %', align: 'right', metricKey: 'supplier_impact_pct' },
      { key: 'spend_coverage_pct', label: 'Coverage', align: 'right', metricKey: 'supplier_impact_coverage' },
    ] : []),
  ] : [];

  const sortedSuppliers = supplierImpact ? [...supplierImpact.suppliers].sort((a, b) => {
    // Lane 121 item 7: signed directional sort -- desc = biggest increases first.
    const aVal = supplierImpactSortKey === 'total_impact' ? (a[supplierImpactSortKey] || 0) : (a[supplierImpactSortKey] || '');
    const bVal = supplierImpactSortKey === 'total_impact' ? (b[supplierImpactSortKey] || 0) : (b[supplierImpactSortKey] || '');
    if (typeof aVal === 'string') {
      return supplierImpactSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return supplierImpactSortDir === 'asc' ? aVal - bVal : bVal - aVal;
  }) : [];

  // PPI divergence (Lane 288). Column order mirrors the file's table idiom.
  const divCols = [
    { key: 'supplier_name', label: 'Supplier', align: 'left' },
    // The divergence header carries the shared "Price vs Market" explainer.
    { key: 'divergence_pp', label: 'Divergence', align: 'right', metricKey: 'supplier_ppi_gap' },
    { key: 'your_change_pct', label: 'Your change', align: 'right' },
    { key: 'market_change_pct', label: 'Market change', align: 'right' },
    { key: 'spend_window', label: 'Spend', align: 'right' },
    { key: 'spend_coverage_pct', label: 'Coverage', align: 'right' },
    // Lane 301 (QA-4): realized-YoY $ net impact -- a COMPANION to the pp
    // divergence, NOT an arithmetic explanation of it. It is the SAME figure as
    // the analytics "Net Impact" table + the dossier "net impact", so it reuses
    // that column's `supplier_impact_net` explainer. Rendered ONLY when the
    // backend echoes price_impact_v2 (v2-gated), so v2 OFF => no column at all.
    ...(ppiDivergence?.price_impact_v2
      ? [{ key: 'net_dollar_impact', label: 'Net YoY $', align: 'right', metricKey: 'supplier_impact_net' }]
      : []),
  ];

  // Direction filter first (client-side), then order: default = the server's
  // |divergence| descending (furthest from market first, both directions); an
  // explicit header click switches to a signed/string client sort.
  const divFiltered = ppiDivergence?.suppliers
    ? ppiDivergence.suppliers.filter(s => ppiDivergenceDir === 'all' || s.direction === ppiDivergenceDir)
    : [];
  const sortedDivergence = ppiDivergenceSortKey
    ? [...divFiltered].sort((a, b) => {
        const aVal = ppiDivergenceSortKey === 'supplier_name' ? (a.supplier_name || '') : (a[ppiDivergenceSortKey] || 0);
        const bVal = ppiDivergenceSortKey === 'supplier_name' ? (b.supplier_name || '') : (b[ppiDivergenceSortKey] || 0);
        if (typeof aVal === 'string') {
          return ppiDivergenceSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        // Signed sort (Lane 121 item 7): on Divergence, desc = biggest above-market first.
        return ppiDivergenceSortDir === 'asc' ? aVal - bVal : bVal - aVal;
      })
    : divFiltered;

  // Category rollup columns + client sort (default = server spend order).
  const catCols = [
    { key: 'category', label: 'Category', align: 'left' },
    { key: 'spend', label: 'Spend', align: 'right' },
    { key: 'material_count', label: 'Materials', align: 'right' },
    { key: 'supplier_count', label: 'Suppliers', align: 'right' },
    { key: 'single_source_count', label: 'Single-source', align: 'right' },
    { key: 'realized_change_pct', label: 'Price change', align: 'right' },
    // "vs Market" carries the shared Price-vs-Market explainer; directional PPI signal.
    { key: 'ppi_divergence_pp', label: 'vs Market', align: 'right', metricKey: 'supplier_ppi_gap' },
  ];
  // Lane 383: the 1%-of-spend fold. Partition on SPEND SHARE (not on sort position),
  // so the hidden set is always "the tiny-spend tail" whatever column the user sorts
  // by -- sorting by Price change must not silently change WHICH categories are hidden.
  const allCategories = categoryRollup?.categories || [];
  const catsAboveThreshold = allCategories.filter(
    (c) => (c.spend_pct || 0) >= CATEGORY_ROLLUP_MIN_SPEND_PCT
  );
  // Defensive: never render an empty table when categories exist. A pathological
  // all-below-threshold shape (or a payload with no spend_pct) falls back to the
  // full list rather than hiding everything.
  const categoryFoldApplies =
    catsAboveThreshold.length > 0 && catsAboveThreshold.length < allCategories.length;
  const categoryFoldedCount = categoryFoldApplies
    ? allCategories.length - catsAboveThreshold.length
    : 0;
  const visibleCategories =
    categoryFoldApplies && !categoryRollupShowAll ? catsAboveThreshold : allCategories;

  const sortedCategories = visibleCategories.length
    ? (categoryRollupSortKey
        ? [...visibleCategories].sort((a, b) => {
            const aVal = categoryRollupSortKey === 'category' ? (a.category_label || a.category || '') : (a[categoryRollupSortKey] || 0);
            const bVal = categoryRollupSortKey === 'category' ? (b.category_label || b.category || '') : (b[categoryRollupSortKey] || 0);
            if (typeof aVal === 'string') {
              return categoryRollupSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            // Signed sort: on Price change / vs Market, desc = biggest increase / above-market first.
            return categoryRollupSortDir === 'asc' ? aVal - bVal : bVal - aVal;
          })
        : visibleCategories)
    : [];

  return (
    <div className="an">
      <div className="an-wrap">

        {/* ═══════ HEADER + SITE FILTER (Lane 5) ═══════ */}
        <header className="an-head">
          <div>
            <h1>Analytics</h1>
            <div className="sub">Spend analysis, supplier metrics, and risk overview</div>
          </div>
          <div className="an-filters">
            <span className="an-lbl" style={{ letterSpacing: '.05em' }}>Site</span>
            <select
              className="an-sel"
              value={siteFilter}
              onChange={handleSiteChange}
              aria-label="Filter by purchase site"
            >
              <option value="">All sites</option>
              {siteOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </header>
        {siteFilter && (
          <div className="an-caveat">
            Showing <strong>{siteFilter}</strong>. Spend, top materials, top suppliers, and single-source
            spend are scoped to this site. Material/supplier counts, PPV, and risk coverage remain company-wide.
          </div>
        )}

        {/* ═══════ KEY FIGURES (figure strip) ═══════ */}
        <section className="an-sec" style={{ marginTop: '22px' }}>
          <div className="an-figs" style={{ gridTemplateColumns: `repeat(${metrics?.has_standard_costs ? 3 : 2}, 1fr)` }}>
            <div className="an-fig">
              <div className="k">Total spend</div>
              <div className="v">{formatCurrencyFull(metrics?.total_spend || 0)}</div>
              <div className="m">Across {pluralize(metrics?.materials_count || 0, 'material')} and {pluralize(metrics?.suppliers_count || 0, 'supplier')}</div>
            </div>
            {metrics?.has_standard_costs && (
              <div className="an-fig">
                <div className="k">Price variance vs standard cost</div>
                <div className="v" style={{ color: ppvStatus === 'under' ? 'var(--good)' : 'var(--crit)' }}>{ppvAmount}</div>
                <div className="m">{ppvStatus === 'under' ? 'Under budget' : 'Over budget'} &middot; net PPV vs standard cost</div>
              </div>
            )}
            <div
              className="an-fig click"
              role="button"
              tabIndex={0}
              onClick={scrollToSingleSource}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollToSingleSource(); } }}
            >
              <div className="k">High-spend single-source</div>
              <div className="v" style={{ color: singleSourceCount > 5 ? 'var(--crit)' : singleSourceCount > 0 ? 'var(--warn)' : 'var(--good)' }}>
                {singleSourceCount} <small>materials</small>
              </div>
              <div className="m">
                {coveragePct < 80 ? `Only ${coveragePct}% contract coverage. Review exposure` : `${coveragePct}% contract coverage`} &middot; view &darr;
              </div>
            </div>
          </div>

          {/* Reconciling line: "Total spend" includes ancillary charges; the
              category/material breakdowns below are charge-excluded. total =
              material + ancillary, so the math visibly closes. Hidden when a
              tenant has no charge lines. */}
          {metrics?.ancillary_charges > 0 && (
            <div className="an-reconcile">
              <strong>Total spend</strong> {formatCurrencyFull(metrics?.total_spend || 0)} = material spend{' '}
              {formatCurrencyFull(metrics?.material_spend || 0)} (excl. ancillary charges) + ancillary charges{' '}
              (tariffs/freight/tooling) {formatCurrencyFull(metrics?.ancillary_charges || 0)}. The category and
              material breakdowns below show material spend.
            </div>
          )}
        </section>

        {/* ═══════ CHARTS ROW (content + form untouched — verbatim recharts) ═══════ */}
        <section className="an-sec">
          <div className="an-charts">

            {/* Spend by Category */}
            <div className="an-panelcard">
              <div className="an-ch-h"><div className="t">Material Spend by Category</div></div>
              <div className="an-ch-sub">
                {categoryChartCapped
                  ? `Top ${CATEGORY_CHART_LIMIT} of ${categoryTotalCount} by spend`
                  : pluralize(categoryTotalCount, 'category', 'categories')} &middot; excl. ancillary charges
              </div>
              {categoryChartData.length > 0 ? (
                /* Lane 306 (QA-4 follow-up, Ryan 2026-07-22): only the TOP 10 categories
                   by spend render, so the chart reads clean/compact (like it did before
                   Lane 301). With <=10 rows a fixed 300px is always ample (30px/row for a
                   24px bar); YAxis width=140 + interval=0 keep labels aligned with bars. */
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={categoryChartData} layout="vertical" margin={{ left: 0, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} vertical={true} />
                    <XAxis
                      type="number"
                      tickFormatter={formatCurrency}
                      tick={{ fontSize: 10, fill: '#9CA3AF' }}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="category_name"
                      width={140}
                      interval={0}
                      tick={{ fontSize: 10, fill: '#9CA3AF', fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value) => [formatCurrencyFull(value), 'Spend']}
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        boxShadow: 'var(--shadow-lg)',
                        fontSize: '11px',
                        color: '#F9FAFB',
                      }}
                      labelStyle={{ color: '#9CA3AF', fontSize: '10px', marginBottom: '4px' }}
                      itemStyle={{ color: '#F9FAFB', padding: '2px 0' }}
                      cursor={{ fill: 'rgba(34, 81, 255, 0.04)' }}
                    />
                    <Bar dataKey="spend" radius={[0, 4, 4, 0]} barSize={24}>
                      {categoryChartData.map((entry, index) => {
                        const opacity = 1 - (index * 0.1);
                        return <Cell key={`cell-${index}`} fill={colors.electricBlue} fillOpacity={Math.max(opacity, 0.3)} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="an-chart-empty">No spend data available</div>
              )}
            </div>

            {/* PPV Trend — only shown when company has standard cost data */}
            {metrics?.has_standard_costs && (
              <div className="an-panelcard">
                <div className="an-ch-h"><div className="t">Monthly PPV Trend</div></div>
                <div className="an-ch-sub">Monthly price variance vs standard cost{siteFilter ? ' · all sites' : ''}</div>
                {ppvTrend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={ppvTrend} margin={{ left: 0, right: 16 }}>
                      <defs>
                        <linearGradient id="ppvGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={colors.electricBlue} stopOpacity={0.2}/>
                          <stop offset="100%" stopColor={colors.electricBlue} stopOpacity={0.02}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 10, fill: '#9CA3AF' }}
                        axisLine={{ stroke: '#E5E7EB' }}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={formatCurrency}
                        tick={{ fontSize: 10, fill: '#9CA3AF' }}
                        width={60}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrencyFull(value), 'PPV']}
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 14px',
                          boxShadow: 'var(--shadow-lg)',
                          fontSize: '11px',
                          color: '#F9FAFB',
                        }}
                        labelStyle={{ color: '#9CA3AF', fontSize: '10px', marginBottom: '4px' }}
                        itemStyle={{ color: '#F9FAFB', padding: '2px 0' }}
                        cursor={{ fill: 'rgba(34, 81, 255, 0.04)' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="total_ppv"
                        stroke={colors.electricBlue}
                        strokeWidth={2}
                        fill="url(#ppvGradient)"
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 0 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="an-chart-empty">No PPV trend data available</div>
                )}
              </div>
            )}

            {/* Top 10 Suppliers by Spend */}
            <div className="an-panelcard">
              <div className="an-ch-h"><div className="t">Top 10 Suppliers by Spend</div></div>
              <div className="an-ch-sub">Trailing 12 months</div>
              {topSuppliers?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topSuppliers} layout="vertical" margin={{ left: 0, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} vertical={true} />
                    <XAxis
                      type="number"
                      tickFormatter={formatCurrency}
                      tick={{ fontSize: 10, fill: '#9CA3AF' }}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="supplier_name"
                      width={140}
                      tick={{ fontSize: 10, fill: '#9CA3AF', fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${formatCurrencyFull(value)} (${props.payload.spend_percentage}%)`,
                        'Spend'
                      ]}
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        boxShadow: 'var(--shadow-lg)',
                        fontSize: '11px',
                        color: '#F9FAFB',
                      }}
                      labelStyle={{ color: '#9CA3AF', fontSize: '10px', marginBottom: '4px' }}
                      itemStyle={{ color: '#F9FAFB', padding: '2px 0' }}
                      cursor={{ fill: 'rgba(34, 81, 255, 0.04)' }}
                    />
                    <Bar dataKey="total_spend" radius={[0, 4, 4, 0]} barSize={20} label={({ x, y, width, height, value, index }) => {
                      const pct = topSuppliers[index]?.spend_percentage || 0;
                      return (
                        <text x={x + width + 6} y={y + height / 2} fill="#6B7280" fontSize={10} dominantBaseline="middle">
                          {formatCurrency(value)} ({pct}%)
                        </text>
                      );
                    }}>
                      {topSuppliers.map((entry, index) => {
                        const opacity = 1 - (index * 0.07);
                        return <Cell key={`supplier-${index}`} fill={colors.electricBlue} fillOpacity={Math.max(opacity, 0.3)} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="an-chart-empty">No supplier spend data available</div>
              )}
            </div>
          </div>
        </section>

        {/* ═══════ TOP MATERIALS ═══════ */}
        <section className="an-sec">
          <div className="an-sec-h">
            <span className="an-lbl">Top materials by spend</span>
            <span className="note">{pluralize(topMaterials.length, 'material')} &middot; excl. ancillary charges &middot; row opens the Material Dossier</span>
          </div>
          <div className="an-tblwrap">
            <table className="an-tbl">
              <thead>
                <tr>
                  <th>Material #</th>
                  <th>Material Name</th>
                  <th>Category</th>
                  <th>Supplier(s)</th>
                  <th className="right">Spend</th>
                </tr>
              </thead>
              <tbody>
                {topMaterials.map((row, idx) => {
                  const extra = (row.supplier_count || 0) > 1 ? ` +${row.supplier_count - 1} more` : '';
                  return (
                    <tr key={idx} className="click" onClick={() => navigate(`/materials/${row.material_number}`)}>
                      <td><span className="an-mno">{row.material_number}</span></td>
                      <td><span className="an-cellname">{row.material_name}</span></td>
                      <td className="an-cellmuted">{row.category_label || row.category}</td>
                      <td>
                        {row.top_supplier
                          ? <span className="an-supcell">{row.top_supplier}{extra && <span className="an-cellmuted">{extra}</span>}</span>
                          : <span className="an-cellmuted">Not available</span>}
                      </td>
                      <td className="num" style={{ fontWeight: 600 }}>{formatCurrency(row.total_spend)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* ═══════ PRICE MOVERS (Feature 19) ═══════ */}
        <section className="an-sec">
          <div className="an-sec-h">
            <span className="an-lbl">Price movers</span>
            {moversSubtitle && <span className="note">{moversSubtitle}</span>}
            <div className="right">
              <Seg
                options={[{ label: '6mo', value: 6 }, { label: '12mo', value: 12 }, { label: '24mo', value: 24 }, { label: 'All', value: 0 }]}
                value={priceMoversMonths}
                onChange={handlePriceMoversMonthsChange}
                ariaLabel="Price movers period"
              />
            </div>
          </div>

          {/* Lane 862 -- FOUR states. This section is NOT flag-gated, so it renders
              its <section> unconditionally and its loading branch already worked;
              what was missing is the FAILED state. A non-2xx, a network error, or
              the endpoint's HTTP-200-plus-`error` compute failure all used to fall
              through to the empty line below, so the page told a buyer no
              significant price changes existed when we had simply failed to look.
              The failed branch also sits BEFORE the table, so a failed re-fetch
              supersedes the stale rows instead of presenting them as current. */}
          {priceMoversLoading ? (
            <div className="an-empty">Loading price change data...</div>
          ) : priceMoversFailed ? (
            <div className="an-empty">Price change data could not be loaded. Refresh the page to try again.</div>
          ) : !priceMovers || !priceMovers.movers?.length ? (
            <div className="an-empty">No significant price changes detected in the selected period.</div>
          ) : (
            <>
              {/* Lane 135 (QA item 3): the method caption. This table's number is a
                  WITHIN-PERIOD move (first 3 orders vs latest 3), not year-over-year. */}
              <div className="an-sub">First 3 orders vs latest 3 within the selected period, not a year-over-year figure.</div>
              <div className="an-tblwrap">
                {/* Lane 227 (W1-2): only the WIDE 9-column shape (price-impact v2 adds
                    the "Annual Impact" column) needs the tighter `--dense` cells to fit
                    at laptop width; the 8-column v2-off shape has room, so it stays
                    roomy like Top Materials. Density tracks the column count. */}
                <table className={priceMovers.price_impact_v2 ? 'an-tbl an-tbl--dense' : 'an-tbl'}>
                  <thead>
                    <tr>
                      {moversCols.map(col => (
                        <th
                          key={col.key}
                          onClick={() => handlePriceMoversSort(col.key)}
                          className={[col.align === 'right' ? 'right' : '', 'sortable', priceMoversSortKey === col.key ? 'on' : ''].filter(Boolean).join(' ')}
                        >
                          {col.label}
                          {/* Lane 135: the info glyph opens the explainer without triggering the header's sort. */}
                          {col.metricKey && (
                            <span onClick={(e) => e.stopPropagation()} style={{ marginLeft: '4px', display: 'inline-flex', verticalAlign: 'middle' }}>
                              <MetricInfo metricKey={col.metricKey} />
                            </span>
                          )}
                          {priceMoversSortKey === col.key && (
                            <span className="an-arrow">{priceMoversSortDir === 'asc' ? '▲' : '▼'}</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMovers.map((mover, idx) => {
                      const isIncrease = mover.change_pct > 0;
                      return (
                        <tr key={idx} className="click" onClick={() => navigate(`/materials/${mover.material_number}`)}>
                          <td><span className="an-mno">{mover.material_number}</span></td>
                          <td><span className="an-cellname">{mover.material_name}</span></td>
                          <td className="an-cellmuted">{mover.material_type_label || mover.material_type}</td>
                          <td><span className="an-supcell">{mover.supplier_name}</span></td>
                          <td className="num">{(mover.l12m_spend || 0) > 0 ? formatCurrency(mover.l12m_spend) : 'None'}</td>
                          <td className="num">${(mover.early_avg_price || 0).toFixed(2)}</td>
                          <td className="num">${(mover.recent_avg_price || 0).toFixed(2)}</td>
                          <td className="num" style={{ fontWeight: 600, color: isIncrease ? 'var(--up)' : 'var(--down)' }}>
                            {isIncrease ? '+' : ''}{(mover.change_pct * 100).toFixed(1)}%
                          </td>
                          {priceMovers.price_impact_v2 && (
                            <td className="num" style={{ fontWeight: 600, color: (mover.spend_impact || 0) > 0 ? 'var(--up)' : 'var(--down)' }}>
                              {(mover.spend_impact || 0) > 0 ? '+' : ''}{formatCurrency(mover.spend_impact)}
                              {mover.is_outlier && <span title="Unusual mover - verify" style={{ marginLeft: '4px', color: 'var(--warn)' }}>*</span>}
                              {/* Lane 118 (2C): dagger when the guard excluded an anomalous order from this row's legs. */}
                              <AnomalyExclusionTag show={mover.had_anomaly_exclusion} count={mover.anomaly_excluded_count} exclusions={mover.anomaly_exclusions} />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Lane 135 (QA item 3): on-page legend for the row markers — only the kinds actually rendered above. */}
              <MarkerLegend kinds={[
                ...(priceMovers.price_impact_v2 && priceMovers.movers.some(m => m.is_outlier) ? ['outlier'] : []),
                ...((priceMovers.price_impact_v2 && priceMovers.movers.some(m => m.had_anomaly_exclusion))
                    || (priceMovers.needs_context || []).some(m => m.had_anomaly_exclusion) ? ['exclusion'] : []),
              ]} />
              {/* Lane 118 (2C): "Needs context" fold -- section-off rows (l12m=0 or |impact| > annual spend),
                  visible + counted, never deleted. Renders only under the hygiene path. */}
              {priceMovers.movers_hygiene && priceMovers.needs_context?.length > 0 && (
                <details className="an-details">
                  <summary>
                    Needs context ({(priceMovers.needs_context_counts?.not_purchased_12mo || 0) + (priceMovers.needs_context_counts?.large_vs_spend || 0)}): no purchases in 12 months, or impact larger than annual spend
                  </summary>
                  <div className="an-tblwrap" style={{ marginTop: '8px' }}>
                    {/* Lane 227 (W1-2): the needs-context fold is always 5 columns with
                        room to spare, so it keeps the roomy default (no --dense). */}
                    <table className="an-tbl">
                      <thead>
                        <tr>
                          <th>Material #</th>
                          <th>Material Name</th>
                          <th>Supplier</th>
                          <th className="right">Change %</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priceMovers.needs_context.map((m, idx) => (
                          <tr key={idx} className="click" onClick={() => navigate(`/materials/${m.material_number}`)}>
                            <td><span className="an-mno">{m.material_number}</span></td>
                            <td><span className="an-cellname">{m.material_name}</span></td>
                            <td><span className="an-supcell">{m.supplier_name}</span></td>
                            <td className="num" style={{ fontWeight: 600, color: m.change_pct > 0 ? 'var(--up)' : 'var(--down)' }}>
                              {m.change_pct > 0 ? '+' : ''}{(m.change_pct * 100).toFixed(1)}%
                            </td>
                            <td>
                              <span className={'an-pill ' + (m.reason === 'large_vs_spend' ? 'warn' : 'mut')}>
                                {m.reason === 'large_vs_spend' ? 'Large vs annual spend' : 'No 12-mo purchases'}
                              </span>
                              <AnomalyExclusionTag show={m.had_anomaly_exclusion} count={m.anomaly_excluded_count} exclusions={m.anomaly_exclusions} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          )}
        </section>

        {/* ═══════ SUPPLIER YoY PRICE IMPACT (Feature 18) ═══════ */}
        <section className="an-sec">
          <div className="an-sec-h">
            <span className="an-lbl">Supplier YoY price impact</span>
            {/* Lane 862: suppressed on failure -- the period label comes from the
                last payload we held, and after a failed re-fetch that window no
                longer describes what is on screen. */}
            {!supplierImpactFailed && supplierImpact?.period?.current && (
              <span className="note">{supplierImpact.period.current} vs {supplierImpact.period.prior} &middot; matched materials, current vs prior window</span>
            )}
            <div className="right">
              <Seg
                options={[{ label: '6mo', value: 6 }, { label: '12mo', value: 12 }, { label: '24mo', value: 24 }]}
                value={supplierImpactMonths}
                onChange={handleSupplierImpactMonthsChange}
                ariaLabel="Supplier YoY period"
              />
            </div>
          </div>

          {/* Lane 862 -- FOUR states, same shape as Price movers above. Not
              flag-gated, so the loading branch already worked; the FAILED state is
              the one that was missing. Note the empty branch below can render the
              server's own `data_availability` explanation -- which is a genuine
              statement about the buyer's PO history, and exactly the kind of
              confident claim a compute failure must never be allowed to borrow. */}
          {supplierImpactLoading ? (
            <div className="an-empty">Loading supplier price impact data...</div>
          ) : supplierImpactFailed ? (
            <div className="an-empty">Supplier price impact data could not be loaded. Refresh the page to try again.</div>
          ) : !supplierImpact || !supplierImpact.suppliers?.length ? (
            <div className="an-empty">
              {supplierImpact?.data_availability ? (
                <div>
                  <p>{supplierImpact.data_availability.message}</p>
                  {supplierImpact.data_availability.suggested_window && (
                    <p style={{ color: 'var(--text-2)' }}>
                      PO history: {supplierImpact.data_availability.earliest_po} to {supplierImpact.data_availability.latest_po} (~{supplierImpact.data_availability.months_of_history} months)
                    </p>
                  )}
                </div>
              ) : (
                'No supplier price impact data available for the selected period.'
              )}
            </div>
          ) : (
            <div className="an-tblwrap">
              <table className="an-tbl">
                <thead>
                  <tr>
                    {yoyCols.map(col => (
                      <th
                        key={col.key}
                        onClick={() => handleSupplierImpactSort(col.key)}
                        className={[col.align === 'right' ? 'right' : '', 'sortable', supplierImpactSortKey === col.key ? 'on' : ''].filter(Boolean).join(' ')}
                      >
                        {col.label}
                        {col.metricKey && (
                          <span onClick={(e) => e.stopPropagation()} style={{ marginLeft: '4px', display: 'inline-flex', verticalAlign: 'middle' }}>
                            <MetricInfo metricKey={col.metricKey} />
                          </span>
                        )}
                        {supplierImpactSortKey === col.key && (
                          <span className="an-arrow">{supplierImpactSortDir === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedSuppliers.map((s, idx) => (
                    <tr key={idx} className="click" onClick={() => navigate(`/suppliers/${encodeURIComponent(s.supplier_name)}`)}>
                      <td><span className="an-supcell" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.supplier_name}</span></td>
                      <td className="num">{formatCurrency(s.current_spend)}</td>
                      <td className="right" style={{ color: s.materials_increased > 0 ? 'var(--up)' : 'var(--text-3)' }}>{s.materials_increased}</td>
                      <td className="right" style={{ color: s.materials_decreased > 0 ? 'var(--down)' : 'var(--text-3)' }}>{s.materials_decreased}</td>
                      {supplierImpact.price_impact_v2 && (
                        <>
                          <td className="num" style={{ fontWeight: 600, color: (s.total_impact || 0) > 0 ? 'var(--up)' : (s.total_impact || 0) < 0 ? 'var(--down)' : 'var(--text-3)' }}>
                            {(s.total_impact || 0) > 0 ? '+' : ''}{formatCurrency(s.total_impact)}
                            {s.contains_outlier && <span title="Includes an unusual mover - verify" style={{ marginLeft: '4px', color: 'var(--warn)' }}>*</span>}
                            <AnomalyExclusionTag show={s.contains_anomaly_exclusion} count={s.anomaly_excluded_count} />
                          </td>
                          <td className="num">
                            {s.impact_pct >= 0 ? '+' : ''}{(s.impact_pct || 0).toFixed(1)}%
                            {/* Lane 121 items 4+6: keep the real number; footnote it when it reads beyond a year's spend. */}
                            <MetricFlagTag
                              show={Math.abs(s.impact_pct || 0) > 100 || ((s.current_spend || 0) > 0 && Math.abs(s.total_impact || 0) > s.current_spend)}
                              title={IMPACT_VERIFY_TOOLTIP}
                            />
                          </td>
                          <td className="num" style={{ color: 'var(--text-3)' }}>
                            {(s.spend_coverage_pct || 0).toFixed(1)}%
                            {/* Lane 121 item 5: flag rows whose impact covers < 40% of spend. */}
                            <MetricFlagTag
                              show={(s.spend_coverage_pct || 0) < 40}
                              title={`Covers only ${(s.spend_coverage_pct || 0).toFixed(0)}% of annual spend -- a partial read.`}
                            />
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Lane 135 (QA item 4): on-page legend for the row markers — only the kinds actually rendered. */}
          {/* Lane 862: these two trailing blocks sit OUTSIDE the state ternary, so
              without the !failed guard a failed re-fetch would keep rendering the
              previous payload's legend and dropped-supplier list underneath a
              "could not be loaded" body. */}
          {!supplierImpactFailed && supplierImpact?.price_impact_v2 && supplierImpact.suppliers?.length > 0 && (
            <MarkerLegend kinds={[
              ...(supplierImpact.suppliers.some(s => s.contains_outlier) ? ['outlier'] : []),
              ...(supplierImpact.suppliers.some(s => s.contains_anomaly_exclusion) ? ['exclusion'] : []),
              ...(supplierImpact.suppliers.some(s => Math.abs(s.impact_pct || 0) > 100 || ((s.current_spend || 0) > 0 && Math.abs(s.total_impact || 0) > s.current_spend)) ? ['verify'] : []),
              ...(supplierImpact.suppliers.some(s => (s.spend_coverage_pct || 0) < 40) ? ['coverage'] : []),
            ]} />
          )}
          {/* Lane 96: suppliers the bad-order guard removed entirely because their materials are
              lot-priced. Named here so they are visibly accounted for; deliberately OUTSIDE the
              ranked table so they never read as $0-impact rows. Key absent => renders nothing. */}
          {!supplierImpactFailed && supplierImpact?.dropped_rows?.length > 0 && (
            <div className="an-foot-note" style={{ borderTop: '1px solid var(--hair)', paddingTop: '8px', marginTop: '10px' }}>
              <strong>Not shown - lot-priced (unit price not meaningful): </strong>
              {supplierImpact.dropped_rows.map(d => d.supplier_name).join(', ')}
            </div>
          )}
        </section>

        {/* ═══════ PPI DIVERGENCE (Lane 288) ═══════ */}
        {/* Flag-gated: the endpoint returns {enabled:false} when PPI_DIVERGENCE_VIEW_ENABLED
            is OFF, so nothing renders and the page is byte-identical to today.

            Lane 860 -- FOUR states, and the gate must let three of them through:
              flag off  -> render NOTHING (the deliberate dark-ship behaviour)
              loading   -> header + "Loading ..." placeholder
              failed    -> header + a plain "could not load" line
              empty     -> header + "no profiles yet"
            The old gate was `ppiDivergence?.enabled` alone. `ppiDivergence` is null
            until the fetch RESOLVES, so the whole section -- including its own
            loading placeholder -- was absent for the entire duration of the request
            and then popped in. That made the `ppiDivergenceLoading` branch below
            literally unreachable, and a slow request indistinguishable from a
            missing feature. (Ryan lost most of 2026-08-05 to exactly that.)
            Loading and failed are therefore part of the gate, not just the body.

            Consequence, stated plainly: a flag-OFF tenant briefly sees the header
            while the request is in flight. That window is short by construction --
            the OFF path short-circuits to {enabled:false} with NO DB work -- and it
            is the reverse of the ON path, where the request is slow and the
            placeholder is exactly what was missing. */}
        {(ppiDivergenceLoading || ppiDivergenceFailed || ppiDivergence?.enabled) && (
          <section className="an-sec">
            <div className="an-sec-h">
              <span className="an-lbl">PPI divergence</span>
              <span className="note">Realized price change vs each supplier&rsquo;s market (PPI) benchmark &middot; last {ppiDivergenceMonths} months</span>
              <div className="right">
                <Seg
                  options={[{ label: '6mo', value: 6 }, { label: '12mo', value: 12 }, { label: '24mo', value: 24 }]}
                  value={ppiDivergenceMonths}
                  onChange={handlePpiDivergenceMonthsChange}
                  ariaLabel="PPI divergence period"
                />
              </div>
            </div>

            {ppiDivergenceLoading ? (
              <div className="an-empty">Loading PPI divergence data...</div>
            ) : ppiDivergenceFailed ? (
              // Deliberately plain: this is a section of an analytics page, not an
              // error dialog. It says only what a reader can act on.
              <div className="an-empty">PPI divergence data could not be loaded. Refresh the page to try again.</div>
            ) : !sortedDivergence.length && ppiDivergenceDir === 'all' ? (
              <div className="an-empty">No suppliers have a PPI market profile to measure against yet.</div>
            ) : (
              <>
                {/* Summary strip reconciles by construction: above + below + inline == measured. */}
                <div className="an-sub">
                  Positive = the supplier&rsquo;s prices moved above their market benchmark; ranked by distance from market.
                </div>
                <div className="an-sub" style={{ marginTop: '2px' }}>
                  <strong>{ppiDivergence.summary.measured}</strong> measured
                  {' · '}
                  <span style={{ color: 'var(--up)', fontWeight: 600 }}>{ppiDivergence.summary.above_market} above market</span>
                  {' · '}
                  <span style={{ color: 'var(--down)', fontWeight: 600 }}>{ppiDivergence.summary.below_market} below market</span>
                  {ppiDivergence.summary.inline > 0 && <>{' · '}{ppiDivergence.summary.inline} in line</>}
                </div>
                <div style={{ margin: '10px 0' }}>
                  <Seg
                    options={[{ label: 'All', value: 'all' }, { label: 'Above market', value: 'above' }, { label: 'Below market', value: 'below' }]}
                    value={ppiDivergenceDir}
                    onChange={setPpiDivergenceDir}
                    ariaLabel="PPI divergence direction filter"
                  />
                </div>

                {!sortedDivergence.length ? (
                  <div className="an-empty">No suppliers in this direction for the selected window.</div>
                ) : (
                  <div className="an-tblwrap">
                    <table className="an-tbl">
                      <thead>
                        <tr>
                          {divCols.map(col => (
                            <th
                              key={col.key}
                              onClick={() => handlePpiDivergenceSort(col.key)}
                              className={[col.align === 'right' ? 'right' : '', 'sortable', ppiDivergenceSortKey === col.key ? 'on' : ''].filter(Boolean).join(' ')}
                            >
                              {col.label}
                              {col.metricKey && (
                                <span onClick={(e) => e.stopPropagation()} style={{ marginLeft: '4px', display: 'inline-flex', verticalAlign: 'middle' }}>
                                  <MetricInfo metricKey={col.metricKey} />
                                </span>
                              )}
                              {ppiDivergenceSortKey === col.key && (
                                <span className="an-arrow">{ppiDivergenceSortDir === 'asc' ? '▲' : '▼'}</span>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedDivergence.map((s, idx) => {
                          const divColor = s.direction === 'above' ? 'var(--up)' : s.direction === 'below' ? 'var(--down)' : 'var(--text-3)';
                          return (
                            <tr key={idx} className="click" onClick={() => navigate(`/suppliers/${encodeURIComponent(s.supplier_name)}`)}>
                              <td><span className="an-supcell" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.supplier_name}</span></td>
                              <td className="num" style={{ fontWeight: 600, color: divColor }}>
                                {(s.divergence_pp || 0) > 0 ? '+' : ''}{(s.divergence_pp || 0).toFixed(2)} pp
                              </td>
                              <td className="num" style={{ color: (s.your_change_pct || 0) > 0 ? 'var(--up)' : (s.your_change_pct || 0) < 0 ? 'var(--down)' : 'var(--text-3)' }}>
                                {(s.your_change_pct || 0) > 0 ? '+' : ''}{(s.your_change_pct || 0).toFixed(2)}%
                              </td>
                              <td className="num" style={{ color: 'var(--text-3)' }}>
                                {(s.market_change_pct || 0) > 0 ? '+' : ''}{(s.market_change_pct || 0).toFixed(2)}%
                              </td>
                              <td className="num">{formatCurrency(s.spend_window)}</td>
                              <td className="num" style={{ color: 'var(--text-3)' }}>
                                {(s.spend_coverage_pct || 0).toFixed(0)}%
                                <MetricFlagTag
                                  show={(s.spend_coverage_pct || 0) < 40}
                                  title={`Covers only ${(s.spend_coverage_pct || 0).toFixed(0)}% of this supplier's spend -- a partial read.`}
                                />
                              </td>
                              {/* Lane 301 (QA-4): realized-YoY $ net impact companion (v2-gated).
                                  "—" (null) = no matched materials to measure (NOT $0); a real
                                  $0 = matched materials that netted to zero. Sign-colored like
                                  the Net Impact table (paid more = up/red, saved = down/green). */}
                              {ppiDivergence?.price_impact_v2 && (
                                <td className="num" style={{ fontWeight: 600, color: s.net_dollar_impact == null ? 'var(--text-3)' : (s.net_dollar_impact > 0 ? 'var(--up)' : s.net_dollar_impact < 0 ? 'var(--down)' : 'var(--text-3)') }}>
                                  {s.net_dollar_impact == null ? 'Not available' : `${s.net_dollar_impact > 0 ? '+' : ''}${formatCurrency(s.net_dollar_impact)}`}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* ═══════ CATEGORY ROLLUP (Lane 292) ═══════ */}
        {/* Flag-gated: the endpoint returns {enabled:false} when CATEGORY_ROLLUP_VIEW_ENABLED
            is OFF, so nothing renders and the page is byte-identical to today.

            Lane 862 -- FOUR states, and the gate must let three of them through:
              flag off  -> render NOTHING (the deliberate dark-ship behaviour)
              loading   -> header + "Loading category rollup..."
              failed    -> header + a plain "could not be loaded" line
              empty     -> header + "no category spend to roll up"
            The old gate was `categoryRollup?.enabled` alone -- character for
            character the defect Lane 860 found on PPI divergence, in the section
            immediately above. `categoryRollup` is null until the fetch RESOLVES,
            so the whole section, INCLUDING its own `categoryRollupLoading ?
            <Loading ...>` branch, was absent for the entire duration of the
            request and then popped in. That made the loading branch below
            literally unreachable and a slow request indistinguishable from a
            missing feature -- the confusion that cost Ryan most of 2026-08-05.
            Loading and failed are therefore part of the GATE, not just the body.

            Consequence, stated plainly and identical to divergence's: a flag-OFF
            tenant briefly sees the header while the request is in flight. That
            window is short by construction -- the OFF path short-circuits to
            {enabled:false} with NO DB work -- and it is the reverse of the ON
            path, where the request is slow and the placeholder is what was
            missing. */}
        {(categoryRollupLoading || categoryRollupFailed || categoryRollup?.enabled) && (
          <section className="an-sec">
            <div className="an-sec-h">
              <span className="an-lbl">Category rollup</span>
              <span className="note">Portfolio by category &middot; spend, materials, suppliers, single-source risk, price change &middot; last {categoryRollupMonths} months</span>
              <div className="right">
                <Seg
                  options={[{ label: '6mo', value: 6 }, { label: '12mo', value: 12 }, { label: '24mo', value: 24 }]}
                  value={categoryRollupMonths}
                  onChange={handleCategoryRollupMonthsChange}
                  ariaLabel="Category rollup period"
                />
              </div>
            </div>

            {categoryRollupLoading ? (
              <div className="an-empty">Loading category rollup...</div>
            ) : categoryRollupFailed ? (
              // The endpoint answers a COMPUTE failure with HTTP 200 +
              // {"enabled":true, "categories":[], "error":"Failed to compute
              // category rollup."} (analytics.py get_category_rollup). Before this
              // lane that rendered as the empty line below -- telling a buyer they
              // had no category spend to roll up, which is a claim about their
              // data we had not earned. Deliberately plain: this is a section of an
              // analytics page, not an error dialog.
              <div className="an-empty">Category rollup could not be loaded. Refresh the page to try again.</div>
            ) : !allCategories.length ? (
              <div className="an-empty">No category spend to roll up for the selected window.</div>
            ) : (
              <>
                <div className="an-sub">
                  {/* Lane 383: these three figures are SERVER-computed over ALL
                      categories -- the 1%-of-spend fold below hides rows only and
                      never moves a total. */}
                  <strong>{categoryRollup.summary.category_count}</strong> categories &middot; {formatCurrency(categoryRollup.summary.total_spend)} total spend.
                  {categoryFoldApplies && !categoryRollupShowAll && (
                    <> Showing the {catsAboveThreshold.length} at {CATEGORY_ROLLUP_MIN_SPEND_PCT}% of spend or more.</>
                  )}
                  {' '}Price change is realized (material grain); &ldquo;vs Market&rdquo; is a directional PPI-mapping signal (positive = above market), shown for {categoryRollup.summary.ppi_measured} categories with a PPI benchmark.
                </div>
                <div className="an-tblwrap">
                  <table className="an-tbl">
                    <thead>
                      <tr>
                        {catCols.map(col => (
                          <th
                            key={col.key}
                            onClick={() => handleCategoryRollupSort(col.key)}
                            className={[col.align === 'right' ? 'right' : '', 'sortable', categoryRollupSortKey === col.key ? 'on' : ''].filter(Boolean).join(' ')}
                          >
                            {col.label}
                            {col.metricKey && (
                              <span onClick={(e) => e.stopPropagation()} style={{ marginLeft: '4px', display: 'inline-flex', verticalAlign: 'middle' }}>
                                <MetricInfo metricKey={col.metricKey} />
                              </span>
                            )}
                            {categoryRollupSortKey === col.key && (
                              <span className="an-arrow">{categoryRollupSortDir === 'asc' ? '▲' : '▼'}</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCategories.map((c, idx) => {
                        const pc = c.realized_change_pct;
                        const dv = c.ppi_divergence_pp;
                        return (
                          <tr key={idx}>
                            <td><span className="an-cellname">{c.category_label || c.category}</span></td>
                            <td className="num" style={{ fontWeight: 600 }}>
                              {formatCurrency(c.spend)}
                              <span className="an-cellmuted" style={{ marginLeft: '6px' }}>{formatCategorySpendPct(c.spend_pct)}</span>
                            </td>
                            <td className="num">{c.material_count}</td>
                            <td className="num">{c.supplier_count}</td>
                            <td className="num">{c.single_source_count}</td>
                            <td className="num" style={{ color: pc == null ? 'var(--text-3)' : pc > 0 ? 'var(--up)' : pc < 0 ? 'var(--down)' : 'var(--text-3)' }}>
                              {pc == null ? 'Not available' : `${pc > 0 ? '+' : ''}${pc.toFixed(1)}%`}
                            </td>
                            <td className="num" style={{ color: dv == null ? 'var(--text-3)' : dv > 0 ? 'var(--up)' : dv < 0 ? 'var(--down)' : 'var(--text-3)' }}>
                              {dv == null ? 'Not available' : `${dv > 0 ? '+' : ''}${dv.toFixed(2)} pp`}
                              {dv != null && (
                                <MetricFlagTag
                                  show={(c.ppi_coverage_pct || 0) < 40}
                                  title={`Only ${(c.ppi_coverage_pct || 0).toFixed(0)}% of this category's spend maps to a PPI benchmark. It is a partial, directional read.`}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Lane 383: expand-to-see-more for the sub-1%-of-spend tail. States
                    exactly what is hidden, so a folded table never reads as missing
                    data. Mirrors the Opportunities "Show all N findings" fold. */}
                {categoryFoldedCount > 0 && (
                  <button
                    type="button"
                    className="an-showall"
                    onClick={() => setCategoryRollupShowAll(prev => !prev)}
                  >
                    {categoryRollupShowAll
                      ? 'Show fewer categories'
                      : `Show ${pluralize(categoryFoldedCount, 'more category', 'more categories')}`}
                    <span className="more">
                      &middot; {categoryRollupShowAll
                        ? `back to the ${CATEGORY_ROLLUP_MIN_SPEND_PCT}%-of-spend view`
                        : `each under ${CATEGORY_ROLLUP_MIN_SPEND_PCT}% of spend`}
                    </span>
                  </button>
                )}
              </>
            )}
          </section>
        )}

        {/* ═══════ SINGLE-SOURCE RISK ═══════ */}
        <section className="an-sec" id="single-source-section">
          <div className="an-sec-h">
            <span className="an-lbl">High-spend single-source risk</span>
            <span className="note">{singleSourceTotal} material{singleSourceTotal !== 1 ? 's' : ''} with &gt;$250K annual spend and only one qualified supplier &middot; company-wide</span>
          </div>
          <div className="an-tblwrap">
            <table className="an-tbl">
              <thead>
                <tr>
                  <th>Material #</th>
                  <th>Material Name</th>
                  <th>Category</th>
                  <th>Supplier</th>
                  <th className="center">Risk</th>
                  <th className="right">Annual Spend</th>
                </tr>
              </thead>
              <tbody>
                {singleSourceMaterials.map((row, idx) => (
                  <tr key={idx} className="click" onClick={() => navigate(`/materials/${row.material_number}`)}>
                    <td><span className="an-mno">{row.material_number}</span></td>
                    <td><span className="an-cellname">{row.material_name}</span></td>
                    <td className="an-cellmuted">{row.category_label || row.category}</td>
                    <td>{row.suppliers || 'No suppliers'}</td>
                    <td className="center">
                      <span className={'an-pill ' + (row.risk_level === 'HIGH' ? 'crit' : 'warn')}>{row.risk_level}</span>
                    </td>
                    <td className="num" style={{ fontWeight: 600 }}>{formatCurrency(row.annual_spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </div>
  );
}

// ─── LOCAL COMPONENTS ─────────────────────────────────────────

// Segmented period toggle (Linear). Preserves the pre-Linear window semantics
// (movers: 6/12/24/All=0; YoY: 6/12/24); onChange fires the existing month handler.
function Seg({ options, value, onChange, ariaLabel }) {
  return (
    <div className="an-seg" role="group" aria-label={ariaLabel}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={value === opt.value ? 'on' : ''}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default Analytics;
